import axios, { getData } from "../../../axios";
import { createSlice } from "@reduxjs/toolkit";
import {
  InviteUserInput,
  InviteUserResult,
  inviteUserInputT,
  inviteUserResultT,
  UserRole,
  usersT,
  userT,
} from "dora-contracts";
import * as t from "io-ts";
import * as tPromise from "io-ts-promise";
import { createErrorReportingAsyncThunk } from "../../helpers";
import keyBy from "lodash/keyBy";

type User = t.TypeOf<typeof userT>;

const prefix = "data/dispatchers";

type State = {
  // dispatchers: User[];
  ids: string[];
  entities: Record<string, User>;
};

const initialState: State = { ids: [], entities: {} };

export const loadUsers = createErrorReportingAsyncThunk(
  `${prefix}/load-users`,
  (): Promise<User[]> =>
    axios.get("/api/dispatchers").then(getData).then(tPromise.decode(usersT))
);

const updateTeam = createErrorReportingAsyncThunk(
  `${prefix}/update-teams`,
  async ({
    dispatcherId,
    teamId,
    method,
  }: {
    dispatcherId: string;
    teamId: string;
    method: "put" | "delete";
  }): Promise<User> => {
    return await axios[method](
      `/api/dispatchers/${dispatcherId}/teams/${teamId}`
    )
      .then(getData)
      .then(tPromise.decode(userT));
  }
);

export const removeTeamFromDispatcher = ({
  dispatcherId,
  teamId,
}: {
  dispatcherId: string;
  teamId: string;
}) => updateTeam({ dispatcherId, teamId, method: "delete" });

export const addTeamToDispatcher = ({
  dispatcherId,
  teamId,
}: {
  teamId: string;
  dispatcherId: string;
}) => updateTeam({ teamId, dispatcherId, method: "put" });

// Validation failures (e.g. email already in use) come back as ok: false for
// the dialog to show; only unexpected errors reject.
export const inviteUser = createErrorReportingAsyncThunk(
  `${prefix}/invite-user`,
  (input: InviteUserInput): Promise<InviteUserResult> =>
    axios
      .post("/api/dispatchers", inviteUserInputT.encode(input))
      .then(getData)
      .then(tPromise.decode(inviteUserResultT))
);

export const setRole = createErrorReportingAsyncThunk(
  `${prefix}/set-role`,
  ({
    dispatcherId,
    role,
  }: {
    dispatcherId: string;
    role: UserRole | null;
  }): Promise<User> =>
    axios
      .patch(`/api/dispatchers/${dispatcherId}/role`, { role })
      .then(getData)
      .then(tPromise.decode(userT))
);

export const disableUser = createErrorReportingAsyncThunk(
  `${prefix}/disable-user`,
  ({ dispatcherId }: { dispatcherId: string }): Promise<User> =>
    axios
      .post(`/api/dispatchers/${dispatcherId}/disable`)
      .then(getData)
      .then(tPromise.decode(userT))
);

export const enableUser = createErrorReportingAsyncThunk(
  `${prefix}/enable-user`,
  ({ dispatcherId }: { dispatcherId: string }): Promise<User> =>
    axios
      .post(`/api/dispatchers/${dispatcherId}/enable`)
      .then(getData)
      .then(tPromise.decode(userT))
);

export const setDefaultTeam = createErrorReportingAsyncThunk(
  `${prefix}/set-default-team`,
  ({
    dispatcherId,
    teamId,
  }: {
    teamId: string;
    dispatcherId: string;
  }): Promise<User> => {
    return axios
      .patch(`/api/dispatchers/${dispatcherId}`, {
        defaultTeamId: teamId,
      })
      .then(getData)
      .then(tPromise.decode(userT));
  }
);

const slice = createSlice({
  name: prefix,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadUsers.fulfilled, (state, { payload }) => {
        state.ids = payload.map((x) => x.id);
        state.entities = keyBy(payload, "id");
      })
      .addCase(updateTeam.fulfilled, (state, { payload }) => {
        state.entities[payload.id] = payload;
      })
      .addCase(setDefaultTeam.fulfilled, (state, { payload }) => {
        state.entities[payload.id] = payload;
      })
      .addCase(inviteUser.fulfilled, (state, { payload }) => {
        if (payload.ok) {
          state.ids.push(payload.user.id);
          state.entities[payload.user.id] = payload.user;
        }
      })
      .addCase(setRole.fulfilled, (state, { payload }) => {
        state.entities[payload.id] = payload;
      })
      .addCase(disableUser.fulfilled, (state, { payload }) => {
        state.entities[payload.id] = payload;
      })
      .addCase(enableUser.fulfilled, (state, { payload }) => {
        state.entities[payload.id] = payload;
      });
  },
});

export default slice.reducer;
